import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { type CustomField, FETCH_CUSTOM_FIELDS } from '@interfaces/customField';
import { SubStepTypes } from '@interfaces/workflow';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { UserContext, usePermission } from '@providers/userContextProvider';
import { modifyUpdatingCustomFields } from '@utils/modifyUpdatingCustomFields';
import { isEmpty } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { getEnabledCustomFields } from '../utils';
import { EditGoalVisual } from './editGoal.visual';

const FETCH_USER_GOAL = (derivedCustomFields?: string[]) => gql`
  query fetchUserGoal($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        goals {
          id
          name
          timeHorizon
          targetAmountCents
          householdClientGroup {
            id
            name
          }
          ${
            derivedCustomFields
              ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
            key
            value
            customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format}
            selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
          }`
              : ''
          }
        }
      }
    }
  }
`;
const FETCH_HOUSEHOLD_GOAL = (derivedCustomFields?: string[]) => gql`
  query fetchHouseholdGoal($clientGroupId: SafeString!) {
    fetchClientGroup(input: { clientGroupId: $clientGroupId }) {
      clientGroup {
        primaryUser {
          id: stringId
        }
        goals {
          id
          name
          timeHorizon
          targetAmountCents
          householdClientGroup {
            id
            name
          }
          ${
            derivedCustomFields
              ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
            key
            value
            customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format}
            selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
          }`
              : ''
          }
        }
      }
    }
  }
`;

const FETCH_GOAL = (derivedCustomFields?: string[]) => gql`
  query fetchGoal($goalId: ObjectID!) {
    fetchGoal(goalId: $goalId) {
      goal {
        id
        name
        timeHorizon
        targetAmountCents
        householdClientGroup {
          id
          name
        }
        ${
          derivedCustomFields
            ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format}
          selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
        }`
            : ''
        }
      }
    }
  }
`;

const UPDATE_GOAL = gql`
  mutation updateGoal($input: UpdateGoalInput!) {
    updateGoal(input: $input) {
      goal {
        id
      }
    }
  }
`;

export const EditGoal = ({
  options,
  objectId,
  onNext,
  stepLoading,
  grid = false,
  updateMode = false,
  workflowCompletion,
  objectType = 'USER',
}: {
  options: any;
  objectId: string;
  onNext: () => void;
  stepLoading: boolean;
  grid?: boolean;
  updateMode?: boolean;
  workflowCompletion?: any;
  objectType?: string;
}) => {
  const { activeOrganization } = useContext(UserContext);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const [derivedCustomFields, setDerivedCustomFields] =
    useState<CustomField[]>();
  const [derivedCustomFieldsKeys, setDerivedCustomFieldsKeys] =
    useState<string[]>();
  const canReadCustomFields = usePermission('read:custom_fields');
  const enabledCustomFields = getEnabledCustomFields(options);
  const isNewGoal = !!workflowData.currentGoalId;
  const [goalData, setGoalData] = useState<any>({
    goalId: null,
    name: '',
    timeHorizon: '',
    targetAmountCents: '',
    householdClientGroupId: '',
    customFields: [],
  });

  const { loading: customFieldLoading } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          workflowStep: SubStepTypes.EDIT_GOAL,
          organizationId: activeOrganization?.id ?? undefined,
          keys: !isEmpty(enabledCustomFields) ? enabledCustomFields : undefined,
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !canReadCustomFields || enabledCustomFields.length === 0,
    fetchPolicy: 'no-cache',
    onCompleted: async (res: any) => {
      const customFieldKeys = res?.fetchCustomFields?.customFields?.map(
        (a: any) => a.key,
      );
      setDerivedCustomFields(res?.fetchCustomFields?.customFields);
      if (isNewGoal) {
        await fetchGoal({
          variables: {
            goalId: workflowData.currentGoalId,
          },
          query: customFieldKeys ? FETCH_GOAL(customFieldKeys) : undefined,
        });
      } else {
        await fetchUserGoal({
          variables: {
            ...(objectType === 'HOUSEHOLD'
              ? { clientGroupId: objectId }
              : { userId: objectId }),
          },
          query: customFieldKeys
            ? objectType === 'HOUSEHOLD'
              ? FETCH_HOUSEHOLD_GOAL(customFieldKeys)
              : FETCH_USER_GOAL(customFieldKeys)
            : undefined,
        });
      }
    },
  });

  const [fetchGoal, { data, loading }] = useLazyQuery(FETCH_GOAL(), {
    variables: { goalId: workflowData.currentGoalId },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (derivedCustomFields) {
      const fields = derivedCustomFields.map((a) => a.key);
      setDerivedCustomFieldsKeys(fields);
    }
  }, [derivedCustomFields]);

  const { data: defaultdata } = useQuery(FETCH_GOAL(), {
    variables: { goalId: workflowData.currentGoalId },
    fetchPolicy: 'no-cache',
    skip:
      !isNewGoal || (canReadCustomFields && enabledCustomFields.length !== 0),
  });

  const [fetchUserGoal, { data: userDataResponse }] = useLazyQuery(
    objectType === 'HOUSEHOLD' ? FETCH_HOUSEHOLD_GOAL() : FETCH_USER_GOAL(),
    {
      variables: {
        ...(objectType === 'HOUSEHOLD'
          ? { clientGroupId: objectId }
          : { userId: objectId }),
      },
      fetchPolicy: 'no-cache',
    },
  );

  const { data: userData } = useQuery(
    objectType === 'HOUSEHOLD' ? FETCH_HOUSEHOLD_GOAL() : FETCH_USER_GOAL(),
    {
      variables: {
        ...(objectType === 'HOUSEHOLD'
          ? { clientGroupId: objectId }
          : { userId: objectId }),
      },
      fetchPolicy: 'no-cache',
      skip:
        isNewGoal || (canReadCustomFields && enabledCustomFields.length !== 0),
    },
  );

  const [updateGoal, { loading: updateGoalLoading }] = useMutation(
    UPDATE_GOAL,
    {
      onCompleted: () => {
        onNext();
      },
      variables: {
        input: {
          ...goalData,
          customFields: modifyUpdatingCustomFields(
            goalData?.customFields,
            derivedCustomFields || [],
            derivedCustomFieldsKeys,
          ),
        },
      },
    },
  );

  useEffect(() => {
    if (data || defaultdata) {
      setGoalData({
        goalId: (data ?? defaultdata).fetchGoal.goal.id,
        name: (data ?? defaultdata).fetchGoal.goal.name || undefined,
        // If it is a new goal, do not preselect the option used to stub out the goal creation
        timeHorizon:
          (data ?? defaultdata).fetchGoal.goal.timeHorizon || undefined,
        targetAmountCents:
          (data ?? defaultdata).fetchGoal.goal.targetAmountCents || undefined,
        householdClientGroupId: (data ?? defaultdata).fetchGoal.goal
          ?.householdClientGroup
          ? (data ?? defaultdata).fetchGoal.goal?.householdClientGroup.id
          : undefined,
        customFields: (data ?? defaultdata).fetchGoal.goal?.customFields || [],
      });
    }
  }, [data, isNewGoal, defaultdata]);

  useEffect(() => {
    if (userData || userDataResponse) {
      const firstGoal =
        userData ??
        (objectType === 'HOUSEHOLD'
          ? userDataResponse.fetchClientGroup.clientGroup.goals[0]
          : userDataResponse.fetchUser.user.goals[0]);
      if (!firstGoal) {
        return;
      }
      setGoalData({
        goalId: firstGoal.id,
        name: firstGoal.name || undefined,
        timeHorizon: firstGoal.timeHorizon || undefined,
        targetAmountCents: firstGoal.targetAmountCents || undefined,
        householdClientGroupId: firstGoal?.householdClientGroup
          ? firstGoal?.householdClientGroup.id
          : undefined,
        customFields: firstGoal?.customFields || [],
      });
    }
  }, [userData, onNext, userDataResponse, objectType]);

  useEffect(() => {
    setWorkflowData({
      ...workflowData,
      goalTimeHorizons: options.options,
    });
  }, [options]);

  return (
    <EditGoalVisual
      options={options}
      goalData={goalData}
      updateGoal={setGoalData}
      continueFunc={updateGoal}
      loading={
        customFieldLoading || loading || updateGoalLoading || stepLoading
      }
      grid={grid}
      updateMode={updateMode}
      activeCustomFields={derivedCustomFieldsKeys}
      derivedCustomFields={derivedCustomFields}
      workflowCompletion={workflowCompletion}
      userId={
        objectType === 'HOUSEHOLD'
          ? userDataResponse?.fetchClientGroup?.clientGroup?.primaryUser?.id
          : objectId
      }
    />
  );
};

export default EditGoal;
